import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { joinFamily, joinFamilyAsHouseholdMember, joinFamilyAsMergeTarget, getInvite } from '@coparent/shared/src/services/familyService';
import { ATTR_FIRST_TOUCH_KEY } from '@coparent/shared/src/services/analyticsService';
import { FOUND_VIA_OPTIONS } from '@coparent/shared/src/utils/constants';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { HiMail, HiLockClosed, HiUser, HiTicket } from 'react-icons/hi';
import toast from 'react-hot-toast';
import Logo from '../common/Logo';

const Register = () => {
  const { currentUser, signup, loginWithGoogle, loginWithApple, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showInviteCode, setShowInviteCode] = useState(!!searchParams.get('code'));
  // Optional self-reported acquisition source. Null until the user taps a pill;
  // tapping the selected pill again clears it. Passed to every signup path.
  const [foundVia, setFoundVia] = useState(null);
  const processingInvite = useRef(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      inviteCode: searchParams.get('code') || ''
    }
  });

  // Redirect authenticated users away — but only if not mid-invite-processing
  useEffect(() => {
    if (currentUser && !processingInvite.current && !searchParams.get('code')) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, navigate, searchParams]);

  const processInviteAfterRegistration = async (userId, displayName, code) => {
    try {
      const invite = await getInvite(code);
      const type = invite?.type || 'coparent';

      if (type === 'household') {
        await joinFamilyAsHouseholdMember(userId, displayName, code, invite.householdOf);
        await refreshUserData(userId);
        toast.success('Joined as a household member!');
      } else if (type === 'merge') {
        await joinFamilyAsMergeTarget(userId, displayName, code);
        await refreshUserData(userId);
        toast.success('Account linked successfully!');
      } else {
        const result = await joinFamily(userId, displayName, code);
        await refreshUserData(userId);
        if (result.joinedAs === 'household_member') {
          toast.success('The co-parent role is already filled \u2014 you\'ve been added as a household member.');
        } else {
          toast.success('Joined your co-parent\'s family!');
        }
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Failed to join family. You can enter the code later from Settings.');
      navigate('/onboarding');
    }
  };

  // Read first-touch attribution from sessionStorage. usePageviewPing wrote it
  // when the visitor landed; we forward it to the user doc at signup so the
  // admin metrics rollup can attribute the new account back to its source.
  const readAttribution = () => {
    try {
      const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(ATTR_FIRST_TOUCH_KEY) : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.inviteCode?.trim()) {
        processingInvite.current = true;
      }
      const user = await signup(data.email, data.password, data.displayName, readAttribution(), foundVia);
      toast.success('Account created!');
      if (data.inviteCode?.trim()) {
        await processInviteAfterRegistration(user.uid, data.displayName, data.inviteCode.trim());
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      processingInvite.current = false;
      toast.error(error.message || 'Failed to create account');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const inviteCode = watch('inviteCode');
      if (inviteCode?.trim()) {
        processingInvite.current = true;
      }
      const user = await loginWithGoogle(foundVia);
      toast.success('Welcome!');
      if (inviteCode?.trim()) {
        await processInviteAfterRegistration(user.uid, user.displayName, inviteCode.trim());
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      processingInvite.current = false;
      toast.error(error.message || 'Google sign-in failed');
    }
    setLoading(false);
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    try {
      const inviteCode = watch('inviteCode');
      if (inviteCode?.trim()) {
        processingInvite.current = true;
      }
      const user = await loginWithApple(foundVia);
      toast.success('Welcome!');
      if (inviteCode?.trim()) {
        await processInviteAfterRegistration(user.uid, user.displayName, inviteCode.trim());
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      processingInvite.current = false;
      toast.error(error.message || 'Apple sign-in failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-shared-100/40 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-parent1-100/40 blur-3xl" />
      </div>

      <div className="max-w-sm w-full relative animate-slide-up">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3 mb-5 hover:opacity-80 transition-opacity">
            <Logo size={72} variant="light" />
            <span className="font-display text-3xl font-medium text-canopy tracking-tight">coparent</span>
          </Link>
          <h1 className="text-2xl font-bold text-ink-800 tracking-tight">Create your account</h1>
          <p className="mt-1.5 text-ink-400 text-sm">
            {showInviteCode ? 'Sign up to join your co-parent\'s family' : 'Get started with your family schedule'}
          </p>
        </div>

        <div className="card p-7">
          <button
            onClick={handleAppleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-surface-200 rounded-xl text-ink-700 hover:bg-surface-50 hover:border-surface-300 transition-all duration-200 font-semibold text-sm"
          >
            <FaApple className="text-xl" />
            Continue with Apple
          </button>
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-surface-200 rounded-xl text-ink-700 hover:bg-surface-50 hover:border-surface-300 transition-all duration-200 font-semibold text-sm mt-3"
          >
            <FcGoogle className="text-xl" />
            Continue with Google
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-white text-ink-400 font-medium">or</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Your Name</label>
              <div className="relative">
                <HiUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                <input
                  {...register('displayName', { required: 'Name is required' })}
                  type="text"
                  className="input-base pl-10"
                  placeholder="Your name"
                />
              </div>
              {errors.displayName && <p className="mt-1.5 text-xs text-parent2-600 font-medium">{errors.displayName.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Email</label>
              <div className="relative">
                <HiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
                  })}
                  type="email"
                  className="input-base pl-10"
                  placeholder="you@example.com"
                />
              </div>
              {errors.email && <p className="mt-1.5 text-xs text-parent2-600 font-medium">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Password</label>
              <div className="relative">
                <HiLockClosed className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'At least 6 characters' }
                  })}
                  type="password"
                  className="input-base pl-10"
                  placeholder="Create a password"
                />
              </div>
              {errors.password && <p className="mt-1.5 text-xs text-parent2-600 font-medium">{errors.password.message}</p>}
            </div>

            {/* Invite Code Section */}
            <div className="pt-2">
              {!showInviteCode ? (
                <button
                  type="button"
                  onClick={() => setShowInviteCode(true)}
                  className="text-sm text-canopy hover:text-accent-700 font-semibold transition-colors"
                >
                  Have an invite code from your co-parent?
                </button>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Invite Code</label>
                  <div className="relative">
                    <HiTicket className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                    <input
                      {...register('inviteCode')}
                      type="text"
                      maxLength={6}
                      className="input-base pl-10 uppercase font-mono tracking-widest"
                      placeholder="ABC123"
                      onChange={(e) => {
                        e.target.value = e.target.value.toUpperCase();
                        register('inviteCode').onChange(e);
                      }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-ink-400">Enter the 6-character code your co-parent shared with you</p>
                </div>
              )}
            </div>

            {/* Optional self-reported acquisition source */}
            <div className="pt-2" data-testid="register-found-via">
              <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">
                How did you find us? <span className="text-ink-300 normal-case font-medium">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {FOUND_VIA_OPTIONS.map((opt) => {
                  const selected = foundVia === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFoundVia((prev) => (prev === opt.value ? null : opt.value))}
                      aria-pressed={selected}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                        selected
                          ? 'bg-canopy text-white border-canopy'
                          : 'bg-white text-ink-600 border-surface-200 hover:border-surface-300 hover:bg-surface-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? (watch('inviteCode')?.trim() ? 'Creating account & joining family...' : 'Creating account...') : (watch('inviteCode')?.trim() ? 'Create Account & Join Family' : 'Create Account')}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-400">
            Already have an account?{' '}
            <Link to="/login" className="text-canopy hover:text-accent-700 font-semibold transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
